import { useCallback, useState } from 'react';

import { PROMPT_ACTION } from 'src/data/prompt';
import * as events from 'src/lib/events';

import { MENU_WIDTH } from './lib';

const ICON_SIZE = 40;

export const useMenuSupport = ({ initMenuProps, onAction }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuProps, setMenuProps] = useState(initMenuProps || null);
  const [wasOpened, setWasOpened] = useState(false);
  const [quickSearchMode, setQuickSearchMode] = useState(false);

  const handleMenuButtonClick = useCallback(
    (event, anchorRect, anchorPadding = { left: 0, top: 0 }) => {
      onAction(PROMPT_ACTION.trackEvent, {
        event: events.PROMPTS.promptPlusIconClicked,
      });

      let target = event.currentTarget;

      if (anchorRect?.left && anchorRect?.top) {
        target = document.createElement('div');
        target.setAttribute('class', 'tempMenuAnchor');

        const leftAnchor = anchorRect?.left + anchorPadding.left + ICON_SIZE;

        if (leftAnchor + MENU_WIDTH <= window.innerWidth) {
          target.setAttribute(
            'style',
            `position:absolute;
          left: ${anchorRect.left + anchorPadding.left + ICON_SIZE}px;
          top: ${anchorRect.top + anchorPadding.top}px;`
          );
          setMenuProps((oldProps) => ({
            ...oldProps,
            anchorOrigin: { ...oldProps?.anchorOrigin, horizontal: 'left' },
            transformOrigin: { ...oldProps?.transformOrigin, horizontal: 'left' },
          }));
        } else {
          target.setAttribute(
            'style',
            `position:absolute;
          right: 24px;
          top: ${anchorRect.top + anchorPadding.top}px;`
          );
          setMenuProps((oldProps) => ({
            ...oldProps,
            anchorOrigin: { ...oldProps?.anchorOrigin, horizontal: 'right' },
            transformOrigin: { ...oldProps?.transformOrigin, horizontal: 'right' },
          }));
        }

        document.body.appendChild(target);
      }

      setMenuAnchor(target);
      setMenuVisible(true);
      setMenuProps((oldProps) => ({
        ...oldProps,
        anchorOrigin: { ...oldProps?.anchorOrigin, vertical: 'bottom' },
        transformOrigin: { ...oldProps?.transformOrigin, vertical: 'top' },
      }));
    },
    [onAction]
  );

  const handleCloseMenu = useCallback(() => {
    if (menuAnchor) {
      menuAnchor.focus();
    }
    setQuickSearchMode(false);
    setMenuVisible(false);
    setWasOpened(false);
    const elToRemove = document.querySelectorAll('.tempMenuAnchor');
    elToRemove.forEach((element) => {
      element.remove();
    });
  }, [menuAnchor]);

  const handleMenuPropsChange = useCallback(
    ({ anchorOrigin, transformOrigin }) => {
      setMenuProps((oldProps) => ({
        ...oldProps,
        anchorOrigin: { ...oldProps?.anchorOrigin, ...anchorOrigin },
        transformOrigin: { ...oldProps?.transformOrigin, ...transformOrigin },
      }));
    },
    [setMenuProps]
  );

  const handleOpenMenu = useCallback(() => {
    setMenuVisible(true);
    setWasOpened(true);
  }, []);

  const handleQuickSearchTriggered = useCallback(() => {
    onAction(PROMPT_ACTION.trackEvent, {
      event: events.PROMPTS.promptResourceSearchDisplayed,
      trigger: 'keyboard',
    });

    setQuickSearchMode(true);
  }, [onAction]);

  return {
    menuAnchor,
    menuVisible,
    handleMenuButtonClick,
    handleCloseMenu,
    menuProps,
    handleMenuPropsChange,
    setWasOpened,
    wasOpened,
    handleOpenMenu,
    quickSearchMode,
    handleQuickSearchTriggered,
  };
};
